<script lang="ts">
  import { api, ApiError } from '../../lib/api';
  import { user, type User, type UserSettings } from '../../lib/stores/auth';
  import { showToast } from '../../lib/stores/toast';
  import Card from '../../components/ui/Card.svelte';
  import RadioGroup from '../../components/ui/RadioGroup.svelte';

  // Settings are saved on change (no Save button) — the surface is small
  // enough that a save button would feel like ceremony. We optimistically
  // update the local store first and roll back on server error so the UI
  // stays snappy without lying about persisted state.

  let saving = $state(false);

  // The RadioGroup primitive binds a string value, so the canonical
  // current setting is read here (defaulting to 'miles' when unset) and
  // fed back via the change handler. The two are kept in step by the
  // store update — RadioGroup's two-way binding is overridden on each
  // store re-emit, so optimistic-rollback still wins if the PATCH fails.
  const UNIT_OPTIONS = [
    { value: 'miles' as const, label: 'Miles' },
    { value: 'kilometers' as const, label: 'Kilometers' },
  ];

  async function patchSettings(partial: Partial<UserSettings>): Promise<void> {
    if (saving || !$user) return;
    const previous = $user;
    // Optimistic local update.
    user.set({
      ...previous,
      settings: { ...previous.settings, ...partial },
    });
    saving = true;
    try {
      const res = await api<{ user: User }>('/api/account', {
        method: 'PATCH',
        body: JSON.stringify({ settings: partial }),
      });
      user.set(res.user);
    } catch (err) {
      user.set(previous);
      const msg = err instanceof ApiError ? err.message : "Couldn't save.";
      showToast(msg, 'error');
    } finally {
      saving = false;
    }
  }

  function setUnits(value: 'miles' | 'kilometers'): void {
    if ($user?.settings.unit_system === value) return;
    void patchSettings({ unit_system: value });
  }

  function toggleSound(): void {
    if (!$user) return;
    void patchSettings({ sound_enabled: !$user.settings.sound_enabled });
  }
</script>

{#if $user}
  <div class="account__sections">
    <Card padding="lg">
      <h2 class="account__section-title">Distance units</h2>
      <p class="account__section-body">
        Used for "you guessed 240 miles away" feedback in distance modes.
      </p>
      <RadioGroup
        name="unit_system"
        ariaLabel="Distance units"
        value={$user.settings.unit_system === 'kilometers' ? 'kilometers' : 'miles'}
        options={UNIT_OPTIONS}
        disabled={saving}
        onChange={setUnits}
      />
    </Card>

    <Card padding="lg">
      <h2 class="account__section-title">Sound</h2>
      <p class="account__section-body">
        Plays short feedback tones for correct / incorrect answers.
      </p>
      <label class="account__toggle">
        <input
          type="checkbox"
          checked={$user.settings.sound_enabled !== false}
          onchange={toggleSound}
        />
        <span>Sound effects on</span>
      </label>
    </Card>
  </div>
{/if}
